import { area } from "@turf/area";
import { featureCollection, multiPolygon, polygon } from "@turf/helpers";
import { intersect } from "@turf/intersect";
import { kinks } from "@turf/kinks";
import type { MapVertex, PolygonRings } from "../../utils/area.js";
import { pointInPolygon } from "../../utils/pointInPolygon.js";
import type { Site } from "./site.js";

export const containsPoint = (site: Site, lat: number, long: number): boolean => {
    if (site.polygons && site.polygons[0]) {
        const transformedBoundaries = site.polygons[0].map((p) => [p.lat, p.lng]);
        const point = [lat, long];
        return pointInPolygon(point, transformedBoundaries);
    }
    return false;
};

const toClosedRing = (vertices: readonly MapVertex[]): number[][] | undefined => {
    if (!vertices.every((vertex) => Number.isFinite(vertex.lat) && Number.isFinite(vertex.lng))) return undefined;
    const ring = vertices.map((vertex) => [vertex.lng as number, vertex.lat as number]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first && (first[0] !== last[0] || first[1] !== last[1])) ring.push([...first]);
    return ring.length >= 4 ? ring : undefined;
};

const toMultiPolygon = (rings: number[][][]) => (rings.length ? multiPolygon(rings.map((ring) => [ring])) : undefined);

export const isValidPolygonRing = (vertices: readonly MapVertex[]): boolean => {
    // kinks reports a repeated consecutive vertex as a self-intersection.
    const deduplicated = vertices.filter(
        (vertex, index) =>
            index === 0 || vertex.lat !== vertices[index - 1].lat || vertex.lng !== vertices[index - 1].lng,
    );
    const ring = toClosedRing(deduplicated);
    if (!ring || new Set(ring.map(([lng, lat]) => `${lng},${lat}`)).size < 3) return false;
    try {
        const closed = polygon([ring]);
        // area() alone reads collinear lat/lng points as a sliver; measure the shape the overlap check sees.
        const normalized = intersect(featureCollection([closed, closed]));
        return normalized !== null && area(normalized) > 0 && kinks(closed).features.length === 0;
    } catch {
        return false;
    }
};

export type SiteOverlaps<T> = { overlapping: T[]; failed: { site: T; error: unknown }[] };

export const findOverlappingSites = <T extends { polygons: PolygonRings }>(
    polygons: PolygonRings,
    others: readonly T[],
): SiteOverlaps<T> => {
    const result: SiteOverlaps<T> = { overlapping: [], failed: [] };
    const candidate = toMultiPolygon(polygons.map(toClosedRing).filter((ring) => ring !== undefined));
    if (!candidate) return result;

    others.forEach((other) => {
        try {
            const rings = other.polygons.map(toClosedRing);
            if (!rings.every((ring) => ring !== undefined))
                throw new Error("ring has missing coordinates or under 3 points");
            const existing = toMultiPolygon(rings);
            const overlap = existing && intersect(featureCollection([candidate, existing]));
            if (overlap && area(overlap) > 0) result.overlapping.push(other);
        } catch (error) {
            result.failed.push({ site: other, error });
        }
    });
    return result;
};
